import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { User, Mail, Lock, AlertCircle, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import { pageSeoMeta } from '@shared/pageSeoMeta';
import PhoneInput, { maxDigitsFor } from '../components/PhoneInput';
import { DEFAULT_COUNTRY, type Country } from '../data/countryCodes';

export default function SignupPage() {
  const { user, sendRegisterOtp } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref')?.trim().toUpperCase() || undefined;

  const [form, setForm] = useState({
    email: '',
    phone: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // If already logged in, redirect
  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (form.firstName.trim().length < 3) {
      setError('First name must be at least 3 characters');
      return;
    }
    const cleanPhone = form.phone.replace(/\D/g, '').slice(0, maxDigitsFor(country));
    if (country.iso === 'in') {
      if (cleanPhone.length !== 10) {
        setError('Please enter a valid 10-digit phone number');
        return;
      }
    } else if (!/^\+?\d{7,15}$/.test(`${country.dialCode}${cleanPhone}`)) {
      // Others: mirror the server rule (full number, incl. country code, is 7–15 digits).
      setError('Please enter a valid phone number');
      return;
    }
    setLoading(true);
    try {
      const fullPhone = `${country.dialCode}${cleanPhone}`;
      await sendRegisterOtp({
        email: form.email,
        password: form.password,
        first_name: form.firstName,
        last_name: form.lastName,
        phone: fullPhone,
      });
      // Navigate to verify page, passing form data via location state
      navigate('/verify-email', {
        state: {
          email: form.email,
          password: form.password,
          firstName: form.firstName,
          lastName: form.lastName,
          phone: fullPhone,
          referralCode,
        },
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const inp = `w-full pl-10 pr-4 py-3 rounded-xl border text-sm outline-none transition-colors ${
    isDark
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-emerald-500'
  }`;

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 transition-colors ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <SEO {...pageSeoMeta.signup} />
      <div className={`w-full max-w-md rounded-2xl shadow-xl p-8 ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'}`}>

        <Link to="/" className={`inline-flex items-center gap-1 text-xs mb-6 ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>
          <ArrowLeft size={14} /> Back to Store
        </Link>

        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500 flex items-center justify-center mx-auto mb-4">
            <User className="text-white" size={24} />
          </div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Create Account</h1>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Join ZeroSmoke for the best experience</p>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 text-red-500 p-3 rounded-lg mb-4 text-sm">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>First Name <span className="text-red-500">*</span></label>
              <div className="relative">
                <User size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input type="text" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} placeholder="John" className={inp} required minLength={3} />
              </div>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Last Name <span className="text-red-500">*</span></label>
              <div className="relative">
                <User size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input type="text" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} placeholder="Doe" className={inp} required />
              </div>
            </div>
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Email <span className="text-red-500">*</span></label>
            <div className="relative">
              <Mail size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className={inp} required />
            </div>
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Phone Number <span className="text-red-500">*</span></label>
            <PhoneInput
              country={country}
              onCountryChange={setCountry}
              value={form.phone}
              onChange={phone => setForm({ ...form, phone })}
              isDark={isDark}
              required
            />
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Password <span className="text-red-500">*</span></label>
            <div className="relative">
              <Lock size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                className={inp}
                required
                minLength={6}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading} className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-colors">
            {loading ? 'Please wait...' : 'Create Account'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Already have an account?{' '}
            <Link to="/login" className="text-emerald-500 font-medium hover:underline">Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
